import React, { useState, useEffect, useMemo } from 'react';
import {
  LuTrash2, LuMail, LuPhone, LuUser, LuMessageSquare, LuCalendar,
  LuSearch, LuFilter, LuCircleCheck, LuDownload, LuX, LuHistory, LuClock, LuUserPlus
} from 'react-icons/lu';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';
import ModalPortal from '../../components/ui/ModalPortal';
import EmailReminderModal from '../../components/EmailReminderModal';

const ContactManagement = ({ onCountChange, onOpenAddModal }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [reminderRecipient, setReminderRecipient] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'manual' | 'website'

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sourceFilter, dateFilterMode, startDate, endDate]);

  const fetchContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setContacts(response.data.data);
        if (typeof onCountChange === 'function') {
          onCountChange(response.data.data.length);
        }
      }
    } catch (error) {
      setError('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (error) {
      setError('Failed to delete contact');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected contact(s)?`)) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/bulk-delete`,
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      fetchContacts();
    } catch (error) {
      setError('Failed to delete selected contacts');
    }
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        contact.name?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.phone?.toLowerCase().includes(query) ||
        contact.comment?.toLowerCase().includes(query);

      if (!matchesSearch) return false;

      // Source Filter (All vs Manual vs Website)
      const isManual = Boolean(contact.isManual || contact.addedBy);
      if (sourceFilter === 'manual' && !isManual) return false;
      if (sourceFilter === 'website' && isManual) return false;

      if (dateFilterMode === 'today') {
        return moment(contact.createdAt).isSame(moment(), 'day');
      }
      if (dateFilterMode === 'yesterday') {
        return moment(contact.createdAt).isSame(moment().subtract(1, 'day'), 'day');
      }
      if (dateFilterMode === 'last7') {
        return moment(contact.createdAt).isAfter(moment().subtract(7, 'days').startOf('day'));
      }
      if (dateFilterMode === 'last30') {
        return moment(contact.createdAt).isAfter(moment().subtract(30, 'days').startOf('day'));
      }
      if (dateFilterMode === 'range') {
        let valid = true;
        if (startDate) {
          valid = valid && moment(contact.createdAt).isSameOrAfter(moment(startDate).startOf('day'));
        }
        if (endDate) {
          valid = valid && moment(contact.createdAt).isSameOrBefore(moment(endDate).endOf('day'));
        }
        return valid;
      }

      return true;
    });
  }, [contacts, searchQuery, sourceFilter, dateFilterMode, startDate, endDate]);

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContacts = useMemo(() => {
    return filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredContacts, indexOfFirstItem, indexOfLastItem]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredContacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredContacts.map(r => r._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const csvData = useMemo(() => {
    const dataToExport = selectedIds.length > 0
      ? filteredContacts.filter(r => selectedIds.includes(r._id))
      : filteredContacts;
    return dataToExport.map(r => ({
      Name: r.name,
      Email: r.email,
      Phone: r.phone,
      Message: r.comment,
      SubmittedAt: moment(r.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }));
  }, [filteredContacts, selectedIds]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <StatGrid>
        <StatCard label="Total Contacts" value={contacts.length} icon={LuUser} />
        <StatCard label="Today" value={contacts.filter(r => moment(r.createdAt).isSame(moment(), 'day')).length} icon={LuClock} iconBg="bg-green-100 dark:bg-green-900/30" iconColor="text-green-600 dark:text-green-400" />
        <StatCard label="With Phone" value={contacts.filter(r => r.phone).length} icon={LuPhone} iconBg="bg-blue-100 dark:bg-blue-900/30" iconColor="text-blue-600 dark:text-blue-400" />
        <StatCard label="Filtered" value={filteredContacts.length} icon={LuHistory} iconBg="bg-purple-100 dark:bg-purple-900/30" iconColor="text-purple-600 dark:text-purple-400" />
      </StatGrid>

      <FilterCard>
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full flex items-center gap-3">
            <div className="relative flex-1">
              <LuSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-modern pl-11 py-2.5 text-sm w-full"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-xs font-bold text-gray-800 dark:text-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all whitespace-nowrap"
            >
              <option value="all">All Entry Sources</option>
              <option value="manual">🏷️ Manual Entries Only</option>
              <option value="website">🌐 Website Submissions Only</option>
            </select>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowDateFilters(!showDateFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${showDateFilters || dateFilterMode !== 'all'
                  ? "bg-primary-50 dark:bg-primary-900/30 text-primary-600 border border-primary-200 dark:border-primary-800 font-semibold"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600"
                }`}
            >
              <LuCalendar className="w-4 h-4 text-primary-500" />
              Date Filters {dateFilterMode !== 'all' ? `(${dateFilterMode.toUpperCase()})` : ''}
            </button>
            <button
              onClick={() => setSelectionMode(!selectionMode)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${selectionMode ? "btn-premium !py-2.5" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600"}`}
            >
              <LuCircleCheck className="w-4 h-4" />
              {selectionMode ? "Selection Active" : "Selection Mode"}
            </button>
            <CSVLink
              data={csvData}
              filename={`Contacts_${moment().format('YYYY-MM-DD')}.csv`}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <LuDownload className="w-4 h-4" />
              Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </CSVLink>
          </div>
        </div>

        {showDateFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 flex flex-col md:flex-row gap-4 items-end animate-in fade-in duration-200">
            <div className="w-full md:w-auto">
              <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Date Range Preset</label>
              <select
                value={dateFilterMode}
                onChange={(e) => setDateFilterMode(e.target.value)}
                className="w-full md:w-48 px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white cursor-pointer"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="range">Custom Range</option>
              </select>
            </div>

            {dateFilterMode === 'range' && (
              <>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {dateFilterMode !== 'all' && (
              <button
                type="button"
                onClick={() => {
                  setDateFilterMode('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="px-4 py-2 text-xs font-bold text-red-600 dark:text-red-400 underline hover:no-underline cursor-pointer"
              >
                Reset Date Filter
              </button>
            )}
          </div>
        )}
      </FilterCard>

      {selectionMode && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
              {selectedIds.length} of {filteredContacts.length} selected
            </span>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-bold text-primary-600 dark:text-primary-400 underline hover:no-underline cursor-pointer"
            >
              {selectedIds.length === filteredContacts.length && filteredContacts.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              <LuTrash2 className="w-4 h-4" /> Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      )}

      <DataTable>
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
              {selectionMode && <th className="px-4 py-4 text-center w-12"><input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === filteredContacts.length && filteredContacts.length > 0} className="rounded" /></th>}
              <th className="px-4 py-4 text-left text-sm font-semibold">Name</th>
              <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
              <th className="px-4 py-4 text-left text-sm font-semibold">Phone</th>
              <th className="px-4 py-4 text-left text-sm font-semibold max-w-[180px]">Message</th>
              <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
              <th className="px-4 py-4 text-center text-sm font-semibold w-28">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
            {filteredContacts.length === 0 ? (
              <TableEmpty colSpan={selectionMode ? 7 : 6} preset="search" title="No Contacts Found" description="Try adjusting your search criteria." />
            ) : currentContacts.map((contact) => (
              <tr
                key={contact._id}
                className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer ${selectedIds.includes(contact._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : contact.isManual ? 'bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
                onClick={() => selectionMode ? toggleSelectOne(contact._id) : setSelectedContact(contact)}
              >
                {selectionMode && <td className="px-4 py-3 text-center"><input type="checkbox" checked={selectedIds.includes(contact._id)} onChange={() => toggleSelectOne(contact._id)} onClick={(e) => e.stopPropagation()} className="rounded" /></td>}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white text-sm whitespace-nowrap">{contact.name}</span>
                    {(contact.isManual || contact.addedBy) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs" title={`Added by ${contact.addedBy || 'Admin'}`}>
                        🏷️ Manual
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400 whitespace-nowrap truncate max-w-[220px]" title={contact.email}>{contact.email}</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap truncate max-w-[160px]" title={contact.phone || 'N/A'}>{contact.phone || 'N/A'}</td>
                <td className="px-4 py-3 max-w-[180px] text-sm text-gray-500 whitespace-nowrap truncate" title={contact.comment}>{contact.comment}</td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{moment(contact.createdAt).format('MMM DD, YYYY')}</td>
                <td className="px-4 py-3 text-center whitespace-nowrap w-28">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(contact._id); }}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-xl transition-colors cursor-pointer"
                      title="Delete Contact"
                    >
                      <LuTrash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setReminderRecipient({ name: contact.name, email: contact.email }); }}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-800/50 rounded-xl transition-colors cursor-pointer"
                      title="Set Email Reminder"
                    >
                      <LuMail className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              Showing <span className="font-bold text-gray-900 dark:text-white">{indexOfFirstItem + 1}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min(indexOfLastItem, filteredContacts.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{filteredContacts.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${currentPage === page ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/30' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                      >
                        {page}
                      </button>
                    );
                  }
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-1 text-xs text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </DataTable>

      <ModalPortal open={Boolean(selectedContact)} onClose={() => setSelectedContact(null)}>
        {selectedContact && (
          <>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Contact Details</h2>
              <button onClick={() => setSelectedContact(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl text-gray-500 dark:text-gray-300"><LuX /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              {(selectedContact.isManual || selectedContact.addedBy) && (
                <div className="p-3.5 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-2xl flex items-center justify-between text-xs font-semibold text-indigo-800 dark:text-indigo-300">
                  <span className="flex items-center gap-1.5 font-bold">
                    🏷️ Source: Manually Added Entry
                  </span>
                  <span className="text-[11px] opacity-80">Added by: {selectedContact.addedBy || 'Admin'}</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Name</label><p className="font-bold text-gray-900 dark:text-white">{selectedContact.name}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Email</label><p className="font-bold text-primary-600">{selectedContact.email}</p></div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Message</label><p className="font-medium italic leading-relaxed text-gray-700 dark:text-gray-200">"{selectedContact.comment}"</p></div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3">
              <button onClick={() => setSelectedContact(null)} className="px-6 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold cursor-pointer">Close</button>
              <button onClick={() => { setReminderRecipient({ name: selectedContact.name, email: selectedContact.email }); setSelectedContact(null); }} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold flex items-center gap-2 cursor-pointer"><LuMail className="w-4 h-4" /> Send Reminder</button>
              <button onClick={() => { handleDelete(selectedContact._id); setSelectedContact(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold cursor-pointer">Delete</button>
            </div>
          </>
        )}
      </ModalPortal>

      <EmailReminderModal
        isOpen={Boolean(reminderRecipient)}
        onClose={() => setReminderRecipient(null)}
        recipient={reminderRecipient}
      />
    </div>
  );
};

export default ContactManagement;
